import React, { useState } from "react";

export default function GroupOverlay({
  users,
  selectedUsersForGroup,
  setSelectedUsersForGroup,
  setCreateGroupMode,
  socket,
  currentUser,
}) {
  const [showNameModal, setShowNameModal] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [toast, setToast] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 1800);
  };

  const toggleUserSelection = (chatId) => {
    setSelectedUsersForGroup((prev) =>
      prev.includes(chatId) ? prev.filter((id) => id !== chatId) : [...prev, chatId]
    );
  };

  React.useEffect(() => {
    if (!socket) return;
    const onCreateGroupFailed = ({ reason }) => {
      const map = {
        missing_fields: "Missing required fields",
        invalid_length: "Group name must be 3–50 characters",
        invalid_characters: "Group name contains unsupported characters",
        reserved_group_name: "This group name is reserved",
        group_name_taken: "A group with this name already exists",
      };
      showToast(map[reason] || "Failed to create group");
    };
    socket.on("create-group-failed", onCreateGroupFailed);
    return () => socket.off("create-group-failed", onCreateGroupFailed);
  }, [socket]);

  React.useEffect(() => {
    if (!socket) return;
    const onGroupCreated = () => {
      showToast("Group created");
      setGroupName("");
      setShowNameModal(false);
      setCreateGroupMode(false);
      setSelectedUsersForGroup([]);
    };
    socket.on("group-created", onGroupCreated);
    return () => socket.off("group-created", onGroupCreated);
  }, [socket]);

  const handleCreateGroup = () => {
    if (!groupName.trim()) { showToast("Enter group name"); return; }
    if (!selectedUsersForGroup.length) { showToast("Select at least one member"); return; }
    if (!socket || !currentUser) return;

    const adminChatId = currentUser.chatInfo?.[0]?.chatId;
    const userMap = users.reduce((acc, u) => {
      acc[u.chatId] = u.username ?? u.userName ?? u.name ?? String(u.chatId);
      return acc;
    }, {});

    const creatorChatId = currentUser.chatInfo?.[0]?.chatId;
    const creatorName =
      currentUser?.chatInfo?.[0]?.userName ??
      currentUser?.chatInfo?.[0]?.username ??
      currentUser?.username ??
      currentUser?.name ??
      String(creatorChatId);

    if (creatorChatId) userMap[creatorChatId] = creatorName;

    socket.emit("create-group", {
      name: groupName.trim(),
      adminChatId,
      members: selectedUsersForGroup,
      userMap,
    });
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-end bg-black/40">
      {/* SIDE PANEL — full screen on mobile, drawer on tablet/desktop */}
      <div
        className="w-full sm:max-w-sm h-full bg-white dark:bg-neutral-900 shadow-xl flex flex-col"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        {/* HEADER */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-700"
          style={{ paddingTop: "max(12px, env(safe-area-inset-top, 12px))" }}
        >
          <h3 className="text-base sm:text-lg font-semibold">Select users for new group</h3>
          <button
            onClick={() => { setCreateGroupMode(false); setSelectedUsersForGroup([]); }}
            className="text-2xl text-neutral-500 hover:text-neutral-800 dark:hover:text-white w-9 h-9 flex items-center justify-center touch-manipulation"
          >
            ×
          </button>
        </div>

        {/* SELECTED COUNT CHIP */}
        {selectedUsersForGroup.length > 0 && (
          <div className="px-4 py-2 border-b border-neutral-100 dark:border-neutral-800">
            <span className="text-xs text-green-700 dark:text-green-400 font-medium">
              {selectedUsersForGroup.length} selected
            </span>
          </div>
        )}

        {/* USER LIST */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {users.map((u) => {
            const selected = selectedUsersForGroup.includes(u.chatId);
            return (
              <div
                key={u.chatId}
                onClick={() => toggleUserSelection(u.chatId)}
                className={`
                  flex items-center gap-3 px-4 py-3.5 sm:py-3 cursor-pointer touch-manipulation transition-colors
                  ${selected
                    ? "bg-emerald-50 dark:bg-emerald-900/30"
                    : "hover:bg-neutral-100 dark:hover:bg-neutral-800 active:bg-neutral-200"}
                `}
              >
                <div
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center
                    font-semibold text-white shrink-0
                    ${selected ? "bg-emerald-600" : "bg-neutral-400"}
                  `}
                >
                  {(u.username || "?")[0]?.toUpperCase()}
                </div>
                <div className="flex-1 font-medium text-sm sm:text-base">{u.username}</div>
                <div
                  className={`
                    w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-all
                    ${selected ? "bg-emerald-600 border-emerald-600 scale-110" : "border-neutral-300 dark:border-neutral-600"}
                  `}
                >
                  {selected && <span className="text-white text-xs font-bold leading-none">✓</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* ACTIONS */}
        <div className="px-4 py-3 border-t border-neutral-200 dark:border-neutral-700 flex gap-2">
          <button
            className="flex-1 rounded-lg px-3 py-3 sm:py-2 bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 active:bg-emerald-800 touch-manipulation"
            onClick={() => setShowNameModal(true)}
          >
            Create Group
          </button>
          <button
            className="flex-1 rounded-lg px-3 py-3 sm:py-2 bg-neutral-200 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200 text-sm font-medium touch-manipulation"
            onClick={() => { setCreateGroupMode(false); setSelectedUsersForGroup([]); }}
          >
            Cancel
          </button>
        </div>
      </div>

      {/* GROUP NAME MODAL */}
      {showNameModal && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/50">
          <div
            className="bg-white dark:bg-neutral-900 rounded-t-2xl sm:rounded-2xl shadow-lg w-full sm:max-w-sm p-5"
            style={{ paddingBottom: "max(20px, env(safe-area-inset-bottom, 20px))" }}
          >
            <h3 className="text-lg font-semibold mb-3">Group Name</h3>
            <input
              type="text"
              placeholder="Enter group name..."
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateGroup()}
              className="
                w-full px-3 py-2.5 mb-4 rounded-lg
                border border-neutral-300 dark:border-neutral-700
                bg-white dark:bg-neutral-800
                text-sm outline-none focus:ring-2 focus:ring-emerald-500
              "
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2.5 sm:py-2 rounded-lg bg-neutral-200 dark:bg-neutral-700 text-sm touch-manipulation"
                onClick={() => setShowNameModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2.5 sm:py-2 rounded-lg bg-emerald-600 text-white text-sm touch-manipulation hover:bg-emerald-700"
                onClick={handleCreateGroup}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TOAST */}
      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-emerald-600 text-white px-4 py-2 rounded-full shadow-lg text-sm z-[90]">
          {toast}
        </div>
      )}
    </div>
  );
}